import Decimal from "decimal.js";

import type { NormalizedRow } from "@/types";

/**
 * Pure value-normalization helpers mirroring the Python package's
 * `core/normalizers.py` and the margin math in `core/processor.py`. Monetary
 * values use decimal.js exclusively; floats are never used for money. Every
 * function here is pure (no I/O, no global state).
 */

/** Decimal places monetary values are quantized to (cents). */
export const MONEY_DECIMAL_PLACES = 2;

/** Raised when a value cannot be parsed into its target numeric type. */
export class NormalizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NormalizationError";
  }
}

/** Currency symbol, thousands separators, and whitespace stripped before parsing. */
const STRIP_PATTERN = /[$,\s]/g;

/**
 * Parse a raw monetary value into a `Decimal` without quantizing.
 *
 * Handles a leading currency symbol, thousands separators, surrounding
 * whitespace, and accounting-style negatives written in parentheses
 * (`"(1,234.56)"` → `-1234.56`). Empty or nullish values parse to zero.
 *
 * @throws {NormalizationError} If a non-empty value is not a valid number.
 */
export function parseMoney(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined) return new Decimal(0);
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new NormalizationError(`Monetary value is not finite: ${value}`);
    }
    return new Decimal(value);
  }
  let text = value.trim();
  if (text === "") return new Decimal(0);
  const negative = text.startsWith("(") && text.endsWith(")");
  if (negative) text = text.slice(1, -1);
  text = text.replace(STRIP_PATTERN, "");
  if (text === "") return new Decimal(0);
  let result: Decimal;
  try {
    result = new Decimal(text);
  } catch {
    throw new NormalizationError(`Could not parse a monetary value from ${JSON.stringify(value)}`);
  }
  if (!result.isFinite()) {
    throw new NormalizationError(`Monetary value is not finite: ${JSON.stringify(value)}`);
  }
  return negative ? result.negated() : result;
}

/** Parse and quantize a monetary value to two decimal places (ROUND_HALF_UP). */
export function normalizeMoney(value: string | number | null | undefined): Decimal {
  return parseMoney(value).toDecimalPlaces(MONEY_DECIMAL_PLACES, Decimal.ROUND_HALF_UP);
}

/**
 * Coerce a raw monetary cell into a canonical decimal string, falling back to
 * `"0"` on unparseable input. Used by adapters during mapping so a single bad
 * cell never aborts an in-browser run (the Python CLI raises instead).
 */
export function coerceMoneyString(value: string | null | undefined): string {
  try {
    return parseMoney(value).toString();
  } catch {
    return "0";
  }
}

/**
 * Normalize a raw quantity to an integer. Strips whitespace and thousands
 * separators; fractional inputs truncate toward zero; empty/nullish → `0`.
 * Negative quantities are preserved (a quality rule flags them).
 *
 * @throws {NormalizationError} If a non-empty value is not a valid number.
 */
export function normalizeQuantity(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Math.trunc(value);
  const text = value.replace(STRIP_PATTERN, "");
  if (text === "") return 0;
  let parsed: Decimal;
  try {
    parsed = new Decimal(text);
  } catch {
    throw new NormalizationError(`Could not parse a quantity from ${JSON.stringify(value)}`);
  }
  if (!parsed.isFinite()) {
    throw new NormalizationError(`Quantity is not finite: ${JSON.stringify(value)}`);
  }
  return parsed.trunc().toNumber();
}

/** Coerce a raw quantity cell into an integer, falling back to `0` on failure. */
export function coerceQuantity(value: string | null | undefined): number {
  try {
    return normalizeQuantity(value);
  } catch {
    return 0;
  }
}

/** Unicode "Other" and "Separator" categories — non-printable per Python's `str.isprintable`. */
const NONPRINTABLE = /[\p{C}\p{Z}]/u;

/**
 * Whether `char` is printable. Mirrors Python's `str.isprintable`: characters in
 * the Unicode "Other" or "Separator" categories are non-printable, excepting the
 * ASCII space.
 */
export function isPrintable(char: string): boolean {
  return char === " " ? true : !NONPRINTABLE.test(char);
}

/**
 * Clean a raw vendor SKU: remove a byte-order mark, drop non-printable / control
 * characters, and trim. Internal ASCII spaces are preserved. Returns `null` if
 * nothing printable remains.
 */
export function cleanVendorSku(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const withoutBom = value.replace(/\uFEFF/g, "");
  let printable = "";
  for (const char of withoutBom) {
    if (isPrintable(char)) printable += char;
  }
  const cleaned = printable.trim();
  return cleaned === "" ? null : cleaned;
}

/** Canonicalize a category name: collapse internal whitespace runs and trim. */
export function canonicalizeCategory(name: string): string {
  return name
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .join(" ");
}

const TWELVE_HOUR = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/;
const TWENTY_FOUR_HOUR = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Build an ISO-8601 string from local date parts, or `null` if the date is invalid. */
function buildDate(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
): string | null {
  const date = new Date(year, month - 1, day, hour, minute, second);
  // Reject overflow (e.g. month 13, day 32) that the Date constructor silently rolls over.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date.toISOString();
}

/**
 * Parse a raw "last update" timestamp into an ISO-8601 string. Accepts the
 * ConnectWise export format (`MM/DD/YYYY h:mm am`) plus 24-hour and ISO-like
 * fallbacks. Parsing is lenient: empty/nullish/unrecognized values return `null`
 * rather than throwing, so a malformed date never aborts a run.
 */
export function normalizeDate(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = value.trim();
  if (text === "") return null;

  let match = TWELVE_HOUR.exec(text);
  if (match) {
    let hour = Number(match[4]) % 12;
    if (/[Pp]/.test(match[6]!)) hour += 12;
    return buildDate(Number(match[3]), Number(match[1]), Number(match[2]), hour, Number(match[5]), 0);
  }
  match = TWENTY_FOUR_HOUR.exec(text);
  if (match) {
    return buildDate(
      Number(match[3]),
      Number(match[1]),
      Number(match[2]),
      Number(match[4]),
      Number(match[5]),
      0,
    );
  }
  match = US_DATE.exec(text);
  if (match) {
    return buildDate(Number(match[3]), Number(match[1]), Number(match[2]), 0, 0, 0);
  }
  match = ISO_DATETIME.exec(text);
  if (match) {
    return buildDate(
      Number(match[1]),
      Number(match[2]),
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      match[6] ? Number(match[6]) : 0,
    );
  }
  match = ISO_DATE.exec(text);
  if (match) {
    return buildDate(Number(match[1]), Number(match[2]), Number(match[3]), 0, 0, 0);
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** A row's computed dollar and percentage margins. */
export interface Margins {
  marginDollar: Decimal;
  marginPercent: Decimal | null;
}

/**
 * Compute dollar and percentage margins. Percentage is `null` when price is
 * zero (undefined margin) and is quantized to two decimal places, matching the
 * Python processor.
 */
export function computeMargins(price: Decimal, cost: Decimal): Margins {
  const marginDollar = price.minus(cost);
  const marginPercent = price.isZero()
    ? null
    : marginDollar.div(price).times(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return { marginDollar, marginPercent };
}

/**
 * Fully normalize a mapped row: quantize price/cost, clean the vendor SKU,
 * normalize quantity, canonicalize the category, and compute margins. Monetary
 * fields are emitted as fixed two-decimal strings.
 */
export function normalizeRow(row: NormalizedRow): NormalizedRow {
  const price = normalizeMoney(row.price);
  const cost = normalizeMoney(row.cost);
  const { marginDollar, marginPercent } = computeMargins(price, cost);
  return {
    ...row,
    price: price.toFixed(MONEY_DECIMAL_PLACES),
    cost: cost.toFixed(MONEY_DECIMAL_PLACES),
    vendor_sku: cleanVendorSku(row.vendor_sku),
    on_hand: normalizeQuantity(row.on_hand),
    category: canonicalizeCategory(row.category),
    margin_dollar: marginDollar.toFixed(MONEY_DECIMAL_PLACES),
    margin_percent: marginPercent === null ? null : marginPercent.toFixed(MONEY_DECIMAL_PLACES),
  };
}
